import type { definitions } from '@luxuryescapes/contract-trip'
import type { Moment } from 'moment'
import moment from 'moment'

import { isUuidV4 } from 'lib/string/uuidV4Utils'
import config from 'constants/config'
import {
  DAY_MONTH_NAME_SHORT,
  DMY_CASUAL_SHORT_FORMAT,
  DMY_DATE_FORMAT,
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
  INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_TOUR } from 'constants/offer'
import {
  queryKeyDate,
  queryKeyLanguageId,
  queryKeyPickupPointId,
  queryKeyRedemptionLocationId,
  queryKeyTime,
  urlHashDatePicker,
  urlHashPackageOptions,
} from 'constants/url'
import { nonNullable, sortBy, sortByAll, sum } from 'lib/array/arrayUtils'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import {
  calculateTimezoneOffset,
  convertTZ,
  intlDateFormatter,
  intlDateRangeFormatter,
  intlHyphenDateRangeFormatter,
} from 'lib/datetime/dateUtils'
import { onlyDefinedProperties } from 'lib/object/objectUtils'
import offerPageURL, { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { isBedbankOffer, isCruiseOffer, isLEOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import { isBundleAndSaveOrder } from 'lib/order/orderUtils'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import { capitalise } from 'lib/string/stringUtils'
import { TRIP_IMAGE_IDS } from 'tripPlanner/config'
import type {
  BasicTrip,
  FullTrip,
  Place,
  TravellerRoom,
  TripLocation,
  UserPermissions,
} from 'tripPlanner/types/common'
import type { PublicTripItem } from 'tripPlanner/types/publicTrip'
import type {
  AccommodationItem,
  AttractionItem,
  CruiseItem,
  CustomItem,
  EventItem,
  ExperienceItem,
  FlightItem,
  LEFlightItem,
  LETripItem,
  MapItem,
  NoteItem,
  RestaurantBarItem,
  SavedHotelData,
  TourItem,
  TransportItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from './itemSelection'

const MINUTES_IN_HOUR = 60

export const concatStringsWithComma = (
  ...stringValues: Array<string | undefined>
) => {
  const nonEmptyValues = stringValues.filter(
    (stringValue) => stringValue && stringValue?.trim().length > 0,
  )

  return nonEmptyValues.join(', ') || undefined
}

type CustomAirport = { code: string, name: string }

function airportHasLatLng(
  airport: App.Airport | CustomAirport,
): airport is App.Airport {
  return (
    'latitude' in airport &&
    'longitude' in airport &&
    typeof airport.latitude === 'number' &&
    typeof airport.longitude === 'number'
  )
}

export const convertPlaceFromAirport = (
  airport: App.Airport | CustomAirport,
): Place => {
  if (airportHasLatLng(airport)) {
    return {
      type: 'GEO',
      name: airport.name,
      // we don't have an address from existing details
      address: undefined,
      point: {
        lat: airport.latitude,
        lng: airport.longitude,
      },
      photoUrl: undefined,
    }
  }

  return {
    type: 'CUSTOM',
    name: airport.name,
    // we don't have an address with existing details
    address: undefined, // ToDo: Is there a way of fetching this?
    photoUrl: undefined,
  }
}

export function assertUnreachable(_x: never): never {
  // oxlint-disable-next-line no-console
  console.trace()
  throw new Error("Something wasn't mapped correctly in Trip Planner.")
}

export const isTourOfferNoSummary = (offer: App.AnyOffer): offer is App.Offer =>
  offer.type === OFFER_TYPE_TOUR

export const isBedbankOfferNoSummary = (
  offer: App.AnyOffer,
): offer is App.BedbankOffer => offer.type === OFFER_TYPE_BED_BANK

export const isLEOfferNoSummary = (offer: App.AnyOffer): offer is App.Offer =>
  offer.type !== OFFER_TYPE_BED_BANK

export const isTour = (tripItem: TripItem): tripItem is TourItem =>
  tripItem.type === 'TOUR'

export const isCruise = (tripItem: TripItem): tripItem is CruiseItem =>
  tripItem.type === 'CRUISE'

export const isCruiseV2 = (tripItem: TripItem) =>
  isCruise(tripItem) &&
  !!tripItem.savedItemData &&
  tripItem.savedItemData.offerType === 'cruise'

const isFlight = (tripItem: TripItem): tripItem is FlightItem =>
  tripItem.type === 'FLIGHT'

const isTransport = (tripItem: TripItem): tripItem is TransportItem =>
  tripItem.type === 'BUS' ||
  tripItem.type === 'FERRY' ||
  tripItem.type === 'OTHER_TRANSPORT' ||
  tripItem.type === 'TRAIN'

export type NoEndItem =
  | ExperienceItem
  | EventItem
  | AttractionItem
  | RestaurantBarItem

export const isNoEndItem = (tripItem: TripItem): tripItem is NoEndItem =>
  tripItem.type === 'EXPERIENCE' ||
  tripItem.type === 'EVENT' ||
  tripItem.type === 'ATTRACTION' ||
  tripItem.type === 'RESTAURANT_BAR'

export const isCustomItem = (tripItem: TripItem): tripItem is CustomItem =>
  tripItem.sourceType === 'NA' || tripItem.sourceType === 'Explore'

export const isLeBooking = (tripItem: TripItem) =>
  tripItem.type !== 'NOTE' && !!tripItem.isBooked && !isCustomItem(tripItem)

export const isLeBookmark = (tripItem: TripItem): tripItem is LETripItem =>
  tripItem.type !== 'NOTE' &&
  !tripItem.isBooked &&
  !isCustomItem(tripItem) &&
  !!tripItem.savedItemData

export const isLeItem = (item: TripItem) =>
  isLeBooking(item) || isLeBookmark(item)

export const isLeFlightItem = (
  tripItem: FlightItem,
): tripItem is LEFlightItem =>
  !isCustomItem(tripItem) && !!tripItem.savedItemData

export const getOrderId = (tripItem: TripItem) => {
  if (!isLeBooking(tripItem)) return undefined

  if (!('confirmationCode' in tripItem)) return undefined

  if (!tripItem.confirmationCode) return undefined

  return tripItem.confirmationCode
}

const GOOGLE_MAPS_BASE_URI = 'https://www.google.com/maps/search/?'

export const convertBedbankPropertyAddressToString = (
  address: App.BedbankPropertyAddress,
) => {
  return `${address.line1}  ${address.line2}, ${address.stateProvinceName}, ${address.countryName}`
}

export const googleMapsQueryUrl = (address: string, placeId?: string) => {
  const query = new URLSearchParams(
    onlyDefinedProperties({
      api: '1',
      query: address,
      query_place_id: placeId,
    }),
  )

  // Documentation for URL format: https://developers.google.com/maps/documentation/urls/get-started
  return `${GOOGLE_MAPS_BASE_URI}${query.toString()}`
}

/**
 * Returns the Google Place ID from a GeoPlace, if available.
 * Used exclusively by Google Places API consumers (photos, ratings).
 * Filters out UUIDs — some place types (e.g. bedbank hotels) store
 * supplier property IDs in place.id which are not Google Place IDs.
 */
export const maybePlaceId = (place: Place): string | undefined => {
  if (place.type !== 'GEO' || !place.id) return undefined
  if (isUuidV4(place.id)) return undefined
  return place.id
}

export const getDurationBetweenDates = (
  startDateTime: Moment,
  endDateTime: Moment,
): moment.Duration => {
  return moment.duration(calculateDays(startDateTime, endDateTime))
}

export const calculateTripDaysCount = (
  startDate?: Moment,
  endDate?: Moment,
): string | undefined => {
  if (startDate && endDate) {
    let diff = calculateDays(startDate, endDate)
    if (diff !== undefined) {
      diff += 1
      const intl = getCurrentIntl()
      return intl.formatMessage({
        id: 'AHmlAR_gpdUhbOqmB21o',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Pluralised count of days for the trip duration displayed on the itinerary card',
      }, { count: diff })
    }
  }

  return undefined
}

export const makeDate = (dateString: string): Moment => {
  return moment(dateString)
}

export const makeDateTime = (
  dateString: string,
  timeString: string,
): Moment => {
  return moment(dateString + ' ' + timeString)
}

export function calculateNights(
  startDate?: string,
  endDate?: string,
): number | undefined {
  if (!startDate || !endDate) return
  return moment(endDate)
    .startOf('day')
    .diff(moment(startDate).startOf('day'), 'days')
}

export function calculateDays(
  startDate?: moment.Moment,
  endDate?: moment.Moment,
): number | undefined {
  if (!startDate || !endDate) return
  const start = startDate.clone()
  const end = endDate.clone()
  return end.startOf('day').diff(start.startOf('day'), 'days')
}

export const calculateTravelTimeLabel = (
  startUtcDateTime: moment.Moment | undefined,
  endUtcDateTime: moment.Moment | undefined,
): string | undefined => {
  if (!startUtcDateTime || !endUtcDateTime) {
    return undefined
  }

  const hours = calculateDiff(
    startUtcDateTime,
    endUtcDateTime,
    'hours',
  )
  const minutes = calculateDiff(
    startUtcDateTime,
    endUtcDateTime,
    'minutes',
  )

  const leftoverMinutes = minutes % MINUTES_IN_HOUR

  if (leftoverMinutes) {
    const hoursAndMinutesStrings: Array<string> = []
    if (hours) {
      hoursAndMinutesStrings.push(`${hours}h`)
    }

    hoursAndMinutesStrings.push(`${leftoverMinutes}m`)

    return hoursAndMinutesStrings.join(' ')
  } else {
    const intl = getCurrentIntl()
    return intl.formatMessage(
      {
        id: 'QVfkNdF48j1JdHHX5NVW',
        defaultMessage: '{count, plural, one {# hour} other {# hours}}',
        description: 'Travel time duration in hours on trip planner transport card',
      },
      { count: hours },
    )
  }
}

export function calculateDiff(
  startDateTime: moment.Moment,
  endDateTime: moment.Moment,
  unitOfTime: 'hours' | 'minutes',
): number
export function calculateDiff(
  startDateTime: moment.Moment | undefined,
  endDateTime: moment.Moment | undefined,
  unitOfTime: 'hours' | 'minutes',
): number | undefined {
  if (startDateTime && endDateTime) {
    return endDateTime.diff(startDateTime, unitOfTime)
  }
}

export const calculateHours = (
  startDate: moment.Moment,
  startTime: moment.Moment | undefined,
  endDate: moment.Moment,
  endTime: moment.Moment | undefined,
): number => {
  const startDateTime = startDate.set({
    hour: startTime?.get('hour'),
    minute: startTime?.get('minute'),
  })

  const endDateTime = endDate.set({
    hour: endTime?.get('hour'),
    minute: endTime?.get('minute'),
  })
  return endDateTime.diff(startDateTime, 'hours')
}

export const calculateMinutes = (
  startDate: moment.Moment,
  startTime: moment.Moment | undefined,
  endDate: moment.Moment,
  endTime: moment.Moment | undefined,
): number => {
  const startDateTime = startDate.set({
    hour: startTime?.get('hour'),
    minute: startTime?.get('minute'),
  })

  const endDateTime = endDate.set({
    hour: endTime?.get('hour'),
    minute: endTime?.get('minute'),
  })
  return endDateTime.diff(startDateTime, 'minutes')
}

export const getDurationDays = (
  startDate?: moment.Moment,
  endDate?: moment.Moment,
): string | undefined => {
  const days = calculateDays(startDate, endDate)
  if (typeof days === 'number') {
    const intl = getCurrentIntl()
    return intl.formatMessage({
      id: '6LzaPKe73F3756lyRZ9b',
      defaultMessage: '{count, plural, one {# day} other {# days}}',
      description: 'Duration in days between start and end dates in the trip planner',
    }, { count: days + 1 })
  }
}

export const calculateEndDate = (
  startDate: moment.Moment,
  nights: number,
): moment.Moment => startDate.clone().add(nights, 'days')

export const getTripItemStartDate = (item: TripItem): Moment | undefined => {
  return item.startDate
}

export const getTripItemUTCStartTime = (item: TripItem): Moment | undefined => {
  return item.startUtcDateTime
}

export const getTripItemEndDate = (item: TripItem): Moment | undefined => {
  return 'endDate' in item ? item.endDate : undefined
}

export const getTripItemUTCEndTime = (item: TripItem): Moment | undefined => {
  return 'endUtcDateTime' in item ? item.endUtcDateTime : undefined
}

type RecommendationData = {
  type: 'FLIGHT' | 'HOTEL' | 'EXPERIENCE' | 'ACCOMMODATION'
  location: TripLocation
}

export type ItemSlot = {
  type: 'ITEM'
  item: TripItem
}

export type RecommendationSlot = {
  type: 'RECOMMENDATION'
  recommendation: RecommendationData
}

/** Sort by start time. Put items with no time at the end. */
export const simpleSortTripItems = (items: Array<TripItem>) =>
  sortBy(
    items,
    (item) =>
      getTripItemUTCStartTime(item)?.valueOf() || Number.MAX_SAFE_INTEGER,
    'asc',
  )

export function formatDmytoIsoDate(
  dateString: string | undefined,
): string | undefined {
  if (!dateString) return undefined
  const dateFormat = !dateString.includes('-') ?
    DMY_DATE_FORMAT :
    ISO_DATE_FORMAT

  return moment(dateString, dateFormat).format(ISO_DATE_FORMAT)
}

export function formatDateRange(
  startDate: moment.Moment,
  endDate: moment.Moment,
): string {
  if (!startDate || !endDate) {
    throw new Error(
      'Could not format date - start or end date was null for destination',
    )
  }
  const intl = getCurrentIntl()

  if (endDate.isSame(startDate, 'day')) {
    return intlDateFormatter(
      startDate,
      INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR,
      intl.locale,
    )
  }

  return intlDateRangeFormatter(
    startDate,
    endDate,
    INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR,
    intl.locale,
  )
}

export function formatCasualDateRange(
  startDate: moment.Moment,
  endDate?: moment.Moment,
): string {
  const intl = getCurrentIntl()

  if (!endDate || endDate.isSame(startDate, 'day')) {
    return intlDateFormatter(
      startDate,
      INTL_DMY_CASUAL_SHORT_FORMAT,
      intl.locale,
    )
  }

  return intlHyphenDateRangeFormatter(startDate, endDate, intl.locale)
}

export function formatCasualDateTime(
  dateTime: moment.Moment,
  timeFormat: string,
): string {
  const intl = getCurrentIntl()
  const isThisYear = dateTime.isSame(moment(), 'year')
  const dateFormat = isThisYear ?
    INTL_DAY_MONTH_NAME_SHORT :
    INTL_DMY_CASUAL_SHORT_FORMAT

  return intl.formatMessage(
    {
      id: 'JvFRtOGHb6ueXZMUIWtT',
      defaultMessage: '{dateLabel}, {timeLabel}',
      description: 'Casual date and time label used in trip planner item metadata and trip update timestamps',
    },
    {
      dateLabel: intlDateFormatter(dateTime, dateFormat, intl.locale),
      timeLabel: dateTime.format(timeFormat),
    },
  )
}

export const upperCaseFirstCharOnly = (s: string): string => {
  if (s.length > 0) {
    return `${s[0].toUpperCase()}${s.slice(1)}`
  }

  return s
}

// ToDo: This logic is very crude I need to revisit it once we know it's the right thing to do.
export const convertGoogleAddressComponentsToRegion = (
  addressComponents: Array<google.maps.GeocoderAddressComponent>,
): string | undefined => {
  const addressComponentMap = new Map<
    string,
    Array<google.maps.GeocoderAddressComponent>
  >()

  // Google returns an array of types for every addressComponent, let's group them together and can use it later.
  addressComponents?.forEach((addressComponent) => {
    addressComponent.types.forEach((type) => {
      if (addressComponentMap.has(type)) {
        const currentValues = addressComponentMap.get(type) ?? []
        currentValues.push(addressComponent)
        addressComponentMap.set(type, currentValues)
      } else {
        addressComponentMap.set(type, [addressComponent])
      }
    })
  })

  if (addressComponentMap.size === 0) {
    return
  }

  // Are there any other kind of fields we can look at.
  const possibleCityFields = [
    'administrative_area_level_1',
    'administrative_area_level_2',
    'administrative_area_level_3',
    'administrative_area_level_4',
    'colloquial_area',
    'sublocality',
  ]

  // The very first thing we want to try is
  const regionToken: Array<string> = []

  for (let i = 0; i < possibleCityFields.length; i++) {
    const cityField = possibleCityFields[i]
    if (addressComponentMap.has(cityField)) {
      const addressComponentForCityField = addressComponentMap.get(cityField)

      // ToDo: Is it okay to always first the very first match, not sure how Google orders it.
      if (addressComponentForCityField?.[0]?.long_name) {
        regionToken.push(addressComponentForCityField[0].long_name)
        break
      }
    }
  }

  const countryAddressComponent = addressComponentMap.get('country')
  if (countryAddressComponent) {
    regionToken.push(countryAddressComponent[0].long_name)
  }

  return regionToken.join(', ')
}

export const checkIfTimeIsMidnight = (dateTime: Moment): boolean => {
  return (
    dateTime.hours() === 0 &&
    dateTime.minutes() === 0 &&
    dateTime.seconds() === 0
  )
}

export function isFlightSearchBookmark(item: TripItem): item is FlightItem & { isBooked: false, startTime: undefined, endTime: undefined } {
  return (
    item.sourceType === 'LE_Flight' &&
    !item.isBooked &&
    !item.startTime &&
    !item.endTime
  )
}

/**
 * Return flight search bookmarks are kind of a special case when displaying itinerary items,
 * because they are a single item with a start and end date but actually represent two
 * discrete events: departing flight and returning flight. So we must detect this case and
 * change how the item presentations are generated, so we don't get meaningless intermediary
 * items in the itinerary between the two flights.
 * At some point we should probably rework how these are represented, to have two separate
 * trip items which are tied together in some way.
 */
export function isReturnFlightSearchBookmark(item: TripItem) {
  return (
    isFlightSearchBookmark(item) &&
    item.savedItemData?.fareType === 'return'
  )
}

export const getItemPlace = (
  item: TripItem | PublicTripItem,
  type: 'start' | 'end',
): Place | undefined => {
  switch (item.type) {
    case 'EVENT':
    case 'EXPERIENCE':
    case 'ACCOMMODATION':
    case 'RESTAURANT_BAR':
    case 'ATTRACTION':
      return item.place
    case 'TOUR':
    case 'CRUISE':
    case 'CAR_RENTAL':
    case 'FERRY':
    case 'BUS':
    case 'OTHER_TRANSPORT':
    case 'TRAIN':
    case 'FLIGHT':
    case 'THING_TO_DO':
      return type == 'start' ? item.startPlace : item.endPlace
    case 'NOTE':
      return undefined
    default:
      return assertUnreachable(item)
  }
}

export const getItemConfirmationCode = (item: TripItem): string | undefined => {
  return 'confirmationCode' in item ? item.confirmationCode : undefined
}

export const itemHasPlace = (
  item: TripItem,
): item is Exclude<TripItem, NoteItem> =>
  'place' in item || 'startPlace' in item

export const itemHasLatLng = (item: MapItem): item is MapItem =>
  'place' in item || 'startPlace' in item

export const validateOrderTripAddition = (order?: App.Order): boolean =>
  !!order &&
  order.orderSuccess &&
  [
    order.items,
    order.flightItems,
    order.bedbankItems,
    order.tourItems,
    order.experienceItems,
    order.cruiseItems,
  ].some((items) => items.some(itemNotCancelled)) &&
  !isBundleAndSaveOrder(order)

type TripOrderItem =
  | App.OrderBedbankItem
  | App.Tours.TourV2OrderItem
  | App.OrderExperienceItem
  | App.OrderFlightItem
  | App.OrderItem
  | App.OrderCruiseItem

const itemNotCancelled = (item: TripOrderItem): boolean =>
  item.status !== 'cancelled'

export const typeConstToTitle = (type: TripItem['type']) => {
  return type
    .split('_')
    .map((word: string) => upperCaseFirstCharOnly(word.toLocaleLowerCase()))
    .join(' ')
}

export function formatSingleDateAndTimeRange(
  {
    startDate,
    startDay,
    startTime,
    endTime,
  }: {
    startDate: Moment | undefined
    startDay?: number
    startTime: Moment | undefined
    endTime?: Moment | undefined
  },
  timeFormat: string,
) {
  const intl = getCurrentIntl()
  let dateLabel = ''

  if (startDay) {
    dateLabel = intl.formatMessage(
      {
        id: 'lLHf84QT0QCkQL8eZ71r',
        defaultMessage: 'Day {startDay}',
        description: 'Trip item day number label in planner in the trip planner feature',
      },
      { startDay },
    )
  } else if (startDate) {
    dateLabel = startDate.format(DMY_CASUAL_SHORT_FORMAT)
  }

  if (!startTime) {
    return dateLabel
  }

  const formattedStartTime = startTime.format(timeFormat)
  const formattedEndTime = endTime?.format(timeFormat)

  if (dateLabel && formattedEndTime) {
    return intl.formatMessage(
      {
        id: '61nPqq9EqGioEFwPhOO8',
        defaultMessage: '{dateLabel} · {startTime} - {endTime}',
        description: 'Trip item date with start and end times in planner',
      },
      {
        dateLabel,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
      },
    )
  }

  if (dateLabel) {
    return intl.formatMessage(
      {
        id: 'Jpjd0iiRPBskPt-qA8CS',
        defaultMessage: '{dateLabel} · {startTime}',
        description: 'Trip item date with start time in planner in the trip planner',
      },
      {
        dateLabel,
        startTime: formattedStartTime,
      },
    )
  }

  if (formattedEndTime) {
    return `${formattedStartTime} - ${formattedEndTime}`
  }

  return formattedStartTime
}

export function formatShortDateAndTimeRange({
  startDate,
  startTime,
  endDate,
  endTime,
  timeFormat,
}: {
  startDate: moment.Moment | undefined
  startTime: moment.Moment | undefined
  endDate: moment.Moment | undefined
  endTime: moment.Moment | undefined
  timeFormat: string
}) {
  let dateTimeString = ''

  if (startDate) {
    dateTimeString = startDate.format(DAY_MONTH_NAME_SHORT)
  }

  if (startTime) {
    dateTimeString = [dateTimeString, startTime.format(timeFormat)]
      .filter(Boolean)
      .join(' ')
  }

  if (endDate && endDate.toISOString() !== startDate?.toISOString()) {
    dateTimeString = [dateTimeString, endDate.format(DAY_MONTH_NAME_SHORT)]
      .filter(Boolean)
      .join(' - ')

    if (endTime) {
      dateTimeString = [dateTimeString, endTime.format(timeFormat)]
        .filter(Boolean)
        .join(' ')
    }
  } else {
    if (endTime) {
      dateTimeString = [dateTimeString, endTime.format(timeFormat)]
        .filter(Boolean)
        .join(' - ')
    }
  }
  return dateTimeString
}

export function formatShortDateRange({
  startDate,
  endDate,
  startDay,
  endDay,
  includeYear = true,
}: {
  startDate?: moment.Moment
  endDate?: moment.Moment
  startDay?: number
  endDay?: number
  includeYear?: boolean
}) {
  const intl = getCurrentIntl()
  let dateString = ''

  if (startDay) {
    dateString = intl.formatMessage(
      {
        id: 'CmwjRgoej_V4-qabeWpV',
        defaultMessage: 'Day {startDay}',
        description: 'Trip planner short date label showing a single day number',
      },
      { startDay },
    )
    if (endDay && endDay !== startDay) {
      dateString = intl.formatMessage(
        {
          id: '839JBZiobcfiBcdtOaa8',
          defaultMessage: 'Day {startDay} - Day {endDay}',
          description: 'Trip planner short date range label showing start and end day numbers',
        },
        { startDay, endDay },
      )
    }
    return dateString
  }

  const showYear = includeYear || (startDate && endDate && !startDate.isSame(endDate, 'year'))
  const locale = intl.locale
  const dateFormat = showYear ? INTL_DMY_CASUAL_SHORT_FORMAT : INTL_DAY_MONTH_NAME_SHORT

  if (startDate) {
    if (!endDate || startDate.isSame(endDate, 'date')) {
      return intlDateFormatter(startDate, dateFormat, locale)
    }

    dateString = intlHyphenDateRangeFormatter(startDate, endDate, locale, showYear)
  }

  if (!dateString && endDate) {
    return intlDateFormatter(endDate, dateFormat, locale)
  }

  return dateString
}

export function formatDateTimeOrDateRange(item: TripItem, timeFormat: string) {
  if (item.startDay) {
    return formatDaysAndTime({
      startDay: item.startDay,
      endDay: item.endDay,
      startTime: item.startTime,
      endTime: item.startTime,
      timeFormat,
    })
  }

  if (
    'endDate' in item &&
    item.startDate &&
    item.endDate &&
    !item.startDate?.isSame(item.endDate, 'date')
  ) {
    return formatShortDateRange(item)
  }
  return formatDatesAndDays(item, timeFormat)
}

export function formatDatesAndDays(item: TripItem, timeFormat: string) {
  if (!('endDate' in item) || !item.endDate) {
    return formatSingleDateAndTimeRange(item, timeFormat)
  }

  const intl = getCurrentIntl()
  let formattedString = ''

  if (item.startDate) {
    formattedString = item.startDate.format(DAY_MONTH_NAME_SHORT)
  }

  if (item.startTime) {
    if (
      !item.endDate.isSame(item.startDate) ||
      (item.endTime && !item.endTime.isSame(item.startTime))
    ) {
      formattedString = formattedString ? intl.formatMessage(
        {
          id: 'di0T_gxfKXTSkHs3nNfG',
          defaultMessage: '{dateLabel} · {timeLabel}',
          description: 'Trip item start date with start time in planner in the trip planner',
        },
        {
          dateLabel: formattedString,
          timeLabel: item.startTime.format(timeFormat),
        },
      ) : item.startTime.format(timeFormat)
    }
  }

  if (!item.endDate.isSame(item.startDate)) {
    formattedString += ` - ${item.endDate.format(DMY_CASUAL_SHORT_FORMAT)}`
  }

  if (item.endTime) {
    if (
      !item.endDate.isSame(item.startDate) ||
      !item.endTime.isSame(item.startTime)
    ) {
      formattedString += ` - ${item.endTime.format(timeFormat)}`
    }
  }

  return formattedString
}

// This might be a duplicate of the above now that we've removed PlaceMultiDates
export function formatMultiDatesDays({
  item,
  timeFormat,
}: {
  item: TripItem
  timeFormat: string
}) {
  if (item.startDay) {
    return formatDaysAndTime({
      startDay: item.startDay,
      endDay: item.endDay,
      startTime: item.startTime,
      endTime: item.startTime,
      timeFormat,
    })
  }
  const intl = getCurrentIntl()
  let formattedString = ''

  if (!('endDate' in item) || !item.endDate) {
    return formatSingleDateAndTimeRange(item, timeFormat)
  }

  if (item.startDate) {
    if (item.startDate.isSame(item.endDate, 'year')) {
      formattedString = item.startDate.format(DAY_MONTH_NAME_SHORT)
    } else {
      formattedString = item.startDate.format(DMY_CASUAL_SHORT_FORMAT)
    }
  }

  if (item.startTime) {
    if (
      !item.endDate.isSame(item.startDate) ||
      (item.endTime && !item.endTime.isSame(item.startTime))
    ) {
      formattedString = formattedString ? intl.formatMessage(
        {
          id: 'ldo8zNJWyKS36K-TCH3K',
          defaultMessage: '{dateLabel} · {timeLabel}',
          description: 'Trip item multi-date start date with start time in planner',
        },
        {
          dateLabel: formattedString,
          timeLabel: item.startTime.format(timeFormat),
        },
      ) : item.startTime.format(timeFormat)
    }
  }

  if (!item.endDate.isSame(item.startDate)) {
    formattedString += ` - ${item.endDate.format(DMY_CASUAL_SHORT_FORMAT)}`
  }

  if (item.endTime) {
    if (!item.endDate.isSame(item.startDate)) {
      formattedString = intl.formatMessage(
        {
          id: 'l6GbwQ-62H1YsZ4J0c0G',
          defaultMessage: '{formattedLabel} · {endTimeLabel}',
          description: 'Trip item multi-date label with end time in planner',
        },
        {
          formattedLabel: formattedString,
          endTimeLabel: item.endTime.format(timeFormat),
        },
      )
    } else if (!item.endTime.isSame(item.startTime)) {
      formattedString += ` - ${item.endTime.format(timeFormat)}`
    }
  }

  return formattedString
}

export function formatDurationDaysIfMultipleDays({
  startDate,
  endDate,
}: {
  startDate?: moment.Moment
  endDate?: moment.Moment
}) {
  if (startDate && endDate && !endDate.isSame(startDate, 'day')) {
    return getDurationDays(startDate, endDate)
  }
  return undefined
}

export function formatStartAndEndLocation({
  startPlace,
  endPlace,
}: {
  startPlace: Place
  endPlace?: Place
}) {
  if (!endPlace || startPlace.name === endPlace.name) {
    return startPlace.name
  }
  const intl = getCurrentIntl()
  return intl.formatMessage(
    {
      id: 'GEwAeq3dlGppQ6SPqt8R',
      defaultMessage: 'Starts in {startPlaceName} and ends in {endPlaceName}',
      description: 'Trip planner label showing start and end locations for a multi-city trip',
    },
    { startPlaceName: startPlace.name, endPlaceName: endPlace.name },
  )
}

export function getDayOfText(item: TripItem, date: moment.Moment) {
  const intl = getCurrentIntl()
  if (isTransport(item) || isFlight(item)) {
    return intl.formatMessage({
      id: 'beVpHbyiXw_lV27C0K2l',
      defaultMessage: 'In transit',
      description: 'Trip planner label for transport or flight items indicating travel is in progress',
    })
  }
  const startDate = getTripItemStartDate(item)
  const endDate = getTripItemEndDate(item)
  if (!startDate || !endDate) return
  const days = calculateDays(startDate, endDate)! + 1
  const currentDay = calculateDays(startDate, date)! + 1
  return intl.formatMessage(
    {
      id: 'AcPX1ELo8UwKzktA-B0z',
      defaultMessage: 'Day {currentDay} of {days}',
      description: 'Trip planner label showing current day number out of total days for an item',
    },
    { currentDay, days },
  )
}

export function getNightOfText(
  item: TripItem,
  date: moment.Moment,
): string | undefined {
  const intl = getCurrentIntl()
  if (isTransport(item) || isFlight(item)) {
    return intl.formatMessage({
      id: 'Pnjc1PeOgXpsbpptC6en',
      defaultMessage: 'In transit',
      description: 'Trip planner label for transport or flight items on the night view indicating travel',
    })
  }
  const startDate = getTripItemStartDate(item)
  const endDate = getTripItemEndDate(item)
  if (!startDate || !endDate) return
  const nights = calculateNights(startDate.format(ISO_DATE_FORMAT), endDate.format(ISO_DATE_FORMAT))!
  const currentNight = calculateNights(startDate.format(ISO_DATE_FORMAT), date.format(ISO_DATE_FORMAT))! + 1
  return intl.formatMessage(
    {
      id: 'Y6C1g4MDL0kqVIPcp22a',
      defaultMessage: 'Night {currentNight} of {nights}',
      description: 'Trip planner label showing current night number out of total nights for an item',
    },
    { currentNight, nights },
  )
}

export function convertOfferPropertyGeoDataToRegion(
  geoData: App.OfferPropertyGeoData,
): string {
  let region = ''
  if (geoData.country) {
    if (geoData.locality) {
      region = geoData.locality
    } else if (geoData.administrativeAreaLevel1) {
      region = `${geoData.administrativeAreaLevel1},`
    } else if (geoData.administrativeAreaLevel2) {
      region = `${geoData.administrativeAreaLevel2},`
    } else if (geoData.administrativeAreaLevel3) {
      region = `${geoData.administrativeAreaLevel3},`
    } else if (geoData.administrativeAreaLevel4) {
      region = `${geoData.administrativeAreaLevel4},`
    }

    region += ` ${geoData.country}`
  }

  return region
}

export function convertBedbankPropertyAddressToRegion(
  bedbankPropertyAddress: App.BedbankPropertyAddress,
): string {
  let region = ''
  if (bedbankPropertyAddress.countryName) {
    if (bedbankPropertyAddress.stateProvinceName) {
      region = `${bedbankPropertyAddress.stateProvinceName},`
    } else if (bedbankPropertyAddress.city) {
      region = `${bedbankPropertyAddress.city},`
    }

    region += ` ${bedbankPropertyAddress.countryName}`
  }

  return region
}

export const concatStringWithDot = (...strings: Array<string>): string => {
  const intl = getCurrentIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const stringsCanBeConcatenated = strings.filter(
    (str) => str.trim().length > 0,
  )

  return stringsCanBeConcatenated.join(` ${sep} `)
}

export function getDefaultTripImageId(trips?: Array<BasicTrip>) {
  let imageOptions = TRIP_IMAGE_IDS

  if (trips) {
    // Try to find images that haven't already been used
    const usedImages = new Set(trips.map((trip) => trip.imageId))
    const unusedImages = TRIP_IMAGE_IDS.filter(
      (imageId) => !usedImages.has(imageId),
    )
    if (unusedImages.length > 0) {
      imageOptions = unusedImages
    }
  }

  const defaultImageIndex = Math.floor(Math.random() * imageOptions.length)

  return imageOptions[defaultImageIndex]
}

export function getOfferId(
  item: AccommodationItem | TourItem | CruiseItem,
): string | undefined {
  return 'code' in item ? item.code : undefined
}

interface OfferPagePrefilledStateOptions {
  scrollToOptions?: boolean
}

export const getOfferPagePrefilledStateParams = (
  item: Extract<
    LETripItem,
    TourItem | CruiseItem | AccommodationItem
  >,
  options: OfferPagePrefilledStateOptions = {},
): { searchParams: URLSearchParams, hash: string } => {
  const { scrollToOptions = false } = options
  let startDate: string | undefined
  let endDate: string | undefined
  let occupancies: SavedHotelData['occupancies'] | undefined
  let hash = ''

  if (item.type === 'ACCOMMODATION') {
    startDate = item.savedItemData.checkIn
    endDate = item.savedItemData.checkOut
    occupancies = item.savedItemData.occupancies
    if (scrollToOptions) {
      // Scroll to package options, or open calendar if no dates
      hash = urlHashPackageOptions
      if (!startDate || !endDate) {
        hash = urlHashDatePicker
      }
    }
  } else if (item.type === 'TOUR') {
    startDate = item.savedItemData.startDate
    endDate = item.savedItemData.endDate
    occupancies = item.savedItemData.occupancies
  } else if (item.savedItemData.offerType === 'tour') {
    startDate = item.savedItemData.startDate
    endDate = item.savedItemData.endDate
    occupancies = item.savedItemData.occupancies
  } else {
    throw new Error("Shouldn't be called without associated Legacy Tours")
  }

  const searchParams = new URLSearchParams(
    buildSearchParamsFromFilters({
      checkIn: startDate,
      checkOut: endDate,
      rooms: occupancies,
    }),
  )

  return { searchParams, hash }
}

export const offerPageUrlWithPrefilledState = (
  offer: App.AnyOffer,
  item: Extract<
    LETripItem,
    TourItem | CruiseItem | AccommodationItem
  >,
  options: OfferPagePrefilledStateOptions = {},
): string => {
  const { searchParams, hash } = getOfferPagePrefilledStateParams(item, options)
  return `${offerPageURL(offer, searchParams)}${hash}`
}

export const experiencePageUrlWithPrefilledState = (
  experience: App.ExperienceOffer,
  savedItemData?: definitions['savedExperienceData'],
  scrollToOptions: boolean = true,
): string => {
  const searchParams = new URLSearchParams(
    onlyDefinedProperties({
      [queryKeyDate]: savedItemData?.date,
      [queryKeyTime]: savedItemData?.time,
      [queryKeyPickupPointId]: savedItemData?.pickupPointId,
      [queryKeyRedemptionLocationId]: savedItemData?.redemptionLocationId,
      [queryKeyLanguageId]: savedItemData?.languageId,
    }),
  )

  const hash = scrollToOptions ? '#ticket-options' : ''

  return getExperienceOfferPageURL(experience, searchParams) + hash
}

export function getTimezoneAdjustedTime(
  date: moment.Moment,
  tzString: string,
  amount: moment.DurationInputArg1,
  unit: moment.unitOfTime.DurationConstructor,
) {
  const timezoneOffset = calculateTimezoneOffset(
    date.toDate(),
    convertTZ(date.toDate(), tzString),
  )
  return moment
    .utc(date.format(ISO_DATE_FORMAT), ISO_DATE_FORMAT)
    .subtract(timezoneOffset, 'hours')
    .add(amount, unit)
}

export function getTripURL(tripId: string, sectionId?: string): string {
  return `/trip-planner/trip/${tripId}/itinerary` + (sectionId ? `#${tripItemSelectionId(sectionId)}` : '')
}

export function getTripPermissions(
  trip: BasicTrip | FullTrip,
): UserPermissions {
  return {
    canEdit: trip.role !== 'VIEWER',
    canAdmin: trip.role === 'OWNER' || trip.role === 'ADMIN',
  }
}

export function getCruiseV2OfferUrl(item: CruiseItem) {
  if (!isCruiseV2(item)) {
    throw new Error('Expecting a Cruise V2 item, but got a Cruise V1 item')
  }
  return `/cruises/${item.code}`
}

export function formatDaysAndTime({
  startDay,
  endDay,
  startTime,
  endTime,
  timeFormat,
}: {
  startDay: number | undefined
  endDay: number | undefined
  startTime: moment.Moment | undefined
  endTime: moment.Moment | undefined
  timeFormat: string
}) {
  const intl = getCurrentIntl()
  if (endDay && endDay !== startDay) {
    if (startTime && endTime) {
      return intl.formatMessage(
        {
          id: 'Yl8x5SbK1umMUa4I5xZp',
          defaultMessage: 'Day {startDay} {startTime} - Day {endDay} {endTime}',
          description: 'Trip item multi-day range with start and end times in the trip planner',
        },
        {
          startDay,
          startTime: startTime.format(timeFormat),
          endDay,
          endTime: endTime.format(timeFormat),
        },
      )
    }
    return intl.formatMessage(
      {
        id: 'DvCsA-c7cKlnBOFJoATl',
        defaultMessage: 'Day {startDay} - Day {endDay}',
        description: 'Trip item multi-day range without times in the trip planner',
      },
      { startDay, endDay },
    )
  } else {
    if (startTime && endTime && !endTime.isSame(startTime, 'minute')) {
      return intl.formatMessage(
        {
          id: 'FtZicjZfQxH-_jXyYiFO',
          defaultMessage: 'Day {startDay} · {startTime} - {endTime}',
          description: 'Trip item same-day range with start and end times in planner',
        },
        {
          startDay,
          startTime: startTime.format(timeFormat),
          endTime: endTime.format(timeFormat),
        },
      )
    } else if (startTime) {
      return intl.formatMessage(
        {
          id: 'C-7KXPx7VH-ias_eQF7Q',
          defaultMessage: 'Day {startDay} · {startTime}',
          description: 'Trip item day with start time in planner in the trip planner',
        },
        {
          startDay,
          startTime: startTime.format(timeFormat),
        },
      )
    }
  }
  return intl.formatMessage(
    {
      id: '4FI3FO2mQuORTjLT_bN2',
      defaultMessage: 'Day {startDay}',
      description: 'Trip planner fallback label showing a single day number without time',
    },
    { startDay },
  )
}

export function itemHasDate(item: TripItem) {
  return !!item.startDate || ('endDate' in item && !!item.endDate)
}

export function itemHasTime(item: TripItem) {
  return !!item.startTime || ('endDate' in item && !!item.endTime)
}

export function getItemLatLng(item: MapItem): { lat?: number, lng?: number } {
  switch (item.type) {
    case 'CRUISE':
    case 'BUS':
    case 'CAR_RENTAL':
    case 'FERRY':
    case 'FLIGHT':
    case 'OTHER_TRANSPORT':
    case 'TOUR':
    case 'TRAIN':
    case 'THING_TO_DO':
      if (item.startPlace.type !== 'GEO') {
        return {
          lat: undefined,
          lng: undefined,
        }
      }

      return {
        lat: item.startPlace.point.lat,
        lng: item.startPlace.point.lng,
      }
    case 'ATTRACTION':
    case 'EVENT':
    case 'ACCOMMODATION':
    case 'RESTAURANT_BAR':
    case 'EXPERIENCE':
      if (item.place.type !== 'GEO') {
        return {
          lat: undefined,
          lng: undefined,
        }
      }

      return {
        lat: item.place.point.lat,
        lng: item.place.point.lng,
      }
  }
}

export function itemIsBooked(item: TripItem): boolean {
  return 'isBooked' in item && item.isBooked
}

/** @description [D MMM, 17:00 / 5:00pm, YYYY] */
export const getDateTimeComponents = ({
  date,
  time,
  timeFormat,
}: {
  date: Moment | undefined
  time: Moment | undefined
  timeFormat: string
}) => {
  const formattedDate = date?.format(DAY_MONTH_NAME_SHORT) // D MMM
  const formattedYear = date?.year().toString() // YYYY
  const formattedTime = time?.format(timeFormat) // 17:00 or 5:00pm

  return [formattedDate, formattedTime, formattedYear] as const
}

export const formatDatesAndTimes = <
  T extends {
    startDate?: Moment
    endDate?: Moment
    startTime?: Moment
    endTime?: Moment
    timeFormat: string
  },
>({
  startDate,
  endDate,
  startTime,
  endTime,
  timeFormat,
}: T): string | undefined => {
  const [formattedStartDate, formattedStartTime, formattedStartYear] =
    getDateTimeComponents({
      date: startDate,
      time: startTime,
      timeFormat,
    })

  const [formattedEndDate, formattedEndTime, formattedEndYear] =
    getDateTimeComponents({
      date: endDate,
      time: endTime,
      timeFormat,
    })

  const intl = getCurrentIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)

  let formattedStartString: string = nonNullable([
    formattedStartDate,
    formattedStartTime,
  ]).join(` ${sep} `)

  let formattedEndString: string = nonNullable([
    formattedEndDate,
    formattedEndTime,
  ]).join(` ${sep} `)

  if (formattedStartString.length === 0) {
    // We don't allow end datetime to display without start datetime
    return undefined
  }

  // Once we have valid start and end strings, we can append the year/years appropriately
  const sameYear = formattedStartYear === formattedEndYear
  if (sameYear && formattedStartYear) {
    return (
      nonNullable([formattedStartString, formattedEndString]).join(' - ') +
      ` ${formattedStartYear}`
    )
  }

  if (formattedStartYear) {
    formattedStartString += ` ${formattedStartYear}`
  }

  if (formattedEndYear) {
    formattedEndString += ` ${formattedEndYear}`
  }

  return nonNullable([
    formattedStartString,
    formattedEndString.length > 0 ? formattedEndString : undefined,
  ]).join(' - ')
}

export function getOfferImage(
  offer: App.AnyOffer | undefined,
  otherParameters: {
    tourVariation?: App.Tours.TourV2OfferVariation
  } = {},
): App.Image {
  if (!offer) {
    return {}
  }
  if (isTourV2Offer(offer)) {
    const variationForDisplay = otherParameters.tourVariation ?? Object.values(offer.variations)[0]
    return variationForDisplay?.images[0] ?? {}
  } else if (isCruiseOffer(offer)) {
    return offer.images[0] ?? {}
  } else if ('image' in offer) {
    return offer.image ?? {}
  }
  return 'images' in offer ? offer.images[0] ?? {} : {}
}

export function getOfferImages(
  offer:
    | App.BedbankOffer
    | App.Tours.TourV2Offer
    | App.Tours.TourV2OfferSummary
    | App.Offer
    | App.OfferSummary
    | App.CruiseOffer
    | undefined,
  otherParameters: {
    tourVariation?: App.Tours.TourV2OfferVariation
  } = {},
): Array<App.Image> {
  if (!offer) {
    return []
  }
  if (isTourV2Offer(offer)) {
    const variationForDisplay = otherParameters.tourVariation ?? Object.values(offer.variations)[0]
    return variationForDisplay?.images ?? []
  } else if (isCruiseOffer(offer) || isBedbankOffer(offer) || isLEOffer(offer)) {
    return offer.images ?? []
  }
  return offer.image ? [offer.image] : []
}

export function countTravellers(travellerRooms: Array<TravellerRoom>) {
  const adults = sum(travellerRooms, (travellers) => travellers.adults)
  const children = sum(
    travellerRooms,
    (travellers) => travellers.childAges.length,
  )
  return { adults, children }
}

export function formatTripTravellers(travellerRooms: Array<TravellerRoom>) {
  const { adults, children } = countTravellers(travellerRooms)
  const intl = getCurrentIntl()
  if (children === 0) {
    return intl.formatMessage({
      id: 'ZtCTpSL5dxs9pR-kHbyj',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Number of adult travellers in a trip in the trip planner feature',
    }, { count: adults })
  }
  return intl.formatMessage({
    id: '8QxmwgsEJWmh0GaOqFnW',
    defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
    description: 'Total number of travellers in a trip in the trip planner feature',
  }, { count: adults + children })
}

export function hasTravellers(
  tripTravellers: Array<TravellerRoom> | undefined,
): tripTravellers is [TravellerRoom, ...Array<TravellerRoom>] {
  return (
    !!tripTravellers &&
    tripTravellers.length > 0 &&
    tripTravellers[0].adults > 0
  )
}

export function isFullTrip(trip: BasicTrip | FullTrip): trip is FullTrip {
  return 'items' in trip
}

export function buildMyEscapesOrderLink(item: TripItem): string | undefined {
  const orderId: string | undefined = getOrderId(item)
  if (orderId) {
    return `/account/my-escapes/order/${orderId}`
  }
  return undefined
}

export interface BackButtonConfig {
  to: string
  label: string
}

interface NavigationContext {
  cartId?: string
  orderId?: string
  offer?: App.AnyOffer | App.OrderItemOffer
}

/**
 * Generates back button configuration based on navigation context
 * Returns appropriate URL and label for the back button
 */
export function getBackButtonConfig(context: NavigationContext): BackButtonConfig {
  const intl = getCurrentIntl()
  const { cartId, orderId, offer } = context

  if (cartId) {
    return {
      to: `/checkout-restore-cart?cartId=${cartId}`,
      label: intl.formatMessage({
        id: 'PQzYYXYytohAqxcs9ry7',
        defaultMessage: 'View my cart',
        description: 'Back button label to return to cart from trip planner',
      }),
    }
  } else if (orderId) {
    return {
      to: `/account/my-escapes/order/${orderId}`,
      label: intl.formatMessage({
        id: 'c0NcdFt1HGucqj43VG5D',
        defaultMessage: 'My {purchaseSynonym}s',
        description: 'Back button label to return to order from trip planner',
      }, { purchaseSynonym: capitalise(config.brandConfig.purchaseSynonym) }),
    }
  } else if (offer) {
    return {
      to: offerPageURL(offer),
      label: intl.formatMessage({
        id: 'wWyR2_PGxIcv-dNYtEOI',
        defaultMessage: 'View offer',
        description: 'Back button label to return to offer page from trip planner',
      }),
    }
  } else if (!orderId && !cartId) {
    return {
      to: '/trip-planner',
      label: intl.formatMessage({
        id: 'qhpEwNOEIE2uBA_K5FZP',
        defaultMessage: 'TripPlanner',
        description: 'Back button label that returns the user to the TripPlanner home page',
      }),
    }
  }

  return {
    to: '/',
    label: intl.formatMessage({
      id: '4pYzsegNj005kkCiEuiY',
      defaultMessage: 'Exit',
      description: 'Back button label to exit trip planner and return to homepage',
    }),
  }
}

export function generateGoogleMapsDirectionsUrl(
  destination: { lat: number, lng: number, name?: string, address?: string },
  origin?: { lat: number, lng: number, name?: string, address?: string },
): string {
  const baseUrl = 'https://www.google.com/maps/dir/?api=1'

  let destinationParam: string
  if (destination.name && destination.address) {
    destinationParam = `destination=${encodeURIComponent(`${destination.name}, ${destination.address}`)}`
  } else if (destination.name) {
    destinationParam = `destination=${encodeURIComponent(destination.name)}`
  } else if (destination.address) {
    destinationParam = `destination=${encodeURIComponent(destination.address)}`
  } else {
    destinationParam = `destination=${destination.lat},${destination.lng}`
  }

  let originParam = ''
  if (origin) {
    if (origin.name && origin.address) {
      originParam = `&origin=${encodeURIComponent(`${origin.name}, ${origin.address}`)}`
    } else if (origin.name) {
      originParam = `&origin=${encodeURIComponent(origin.name)}`
    } else if (origin.address) {
      originParam = `&origin=${encodeURIComponent(origin.address)}`
    } else {
      originParam = `&origin=${origin.lat},${origin.lng}`
    }
  }

  return `${baseUrl}&${destinationParam}${originParam}`
}

/**
 * Finds the previous item in a sorted trip itinerary sequence and returns its place details
 * @param tripItems Array of trip items
 * @param targetItemId ID of the current item
 * @returns The place details of the previous item, or accommodation place if no previous item found
 */
export function findPreviousItemPlace(
  tripItems: Array<TripItem>,
  targetItemId: string,
): {
  id?: string
  coordinates?: { lat: number, lng: number }
  name?: string
  address?: string
} | undefined {
  const targetItem = tripItems.find(item => item.id === targetItemId)
  if (!targetItem?.itineraryRankString) return undefined

  const filteredItems = tripItems.filter(item => !!item.itineraryRankString)

  const sortedItems = sortByAll(
    filteredItems,
    [
      {
        selector: item => item.startDate?.valueOf() || 0,
        direction: 'asc',
      },
      {
        selector: item => item.itineraryRankString,
        direction: 'asc',
      },
    ],
  )

  const targetIndex = sortedItems.findIndex(item => item.id === targetItemId)
  if (targetIndex <= 0) {
    // If there's no previous item, look for an accommodation
    return findAccommodationPlace(tripItems, targetItem)
  }

  const previousItem = sortedItems[targetIndex - 1]

  // Check if previous item is from the same day
  if (targetItem.startDate && previousItem.startDate &&
    !targetItem.startDate.isSame(previousItem.startDate, 'day')) {
    // If not from the same day, look for an accommodation
    return findAccommodationPlace(tripItems, targetItem)
  }

  const previousPlace = getItemPlace(previousItem, 'end')

  if (!previousPlace || previousPlace.type === 'CUSTOM') {
    return findAccommodationPlace(tripItems, targetItem)
  }

  return {
    id: previousPlace.id,
    coordinates: previousPlace.point,
    name: previousPlace.name,
    address: previousPlace.address,
  }
}

/**
 * Helper function to find accommodation place for the target item's day
 */
function findAccommodationPlace(
  tripItems: Array<TripItem>,
  targetItem: TripItem,
): {
  id?: string
  coordinates?: { lat: number, lng: number }
  name?: string
  address?: string
} | undefined {
  const accommodation = tripItems.find(item =>
    item.type === 'ACCOMMODATION' &&
    targetItem.startDate &&
    item.startDate &&
    item.endDate &&
    targetItem.startDate >= item.startDate &&
    targetItem.startDate <= item.endDate,
  )

  if (!accommodation) return undefined

  const accommodationPlace = getItemPlace(accommodation, 'start')

  if (!accommodationPlace || accommodationPlace.type === 'CUSTOM') return undefined

  return {
    id: accommodationPlace.id,
    coordinates: accommodationPlace.point,
    name: accommodationPlace.name,
    address: accommodationPlace.address,
  }
}
